import { useState, useEffect } from 'react';
import { getApiUrl } from '../utils/api';
import { User } from '../types';

export interface Notification {
  id: string;
  type: 'approval_request' | 'approved' | 'rejected' | 'deployed' | 'testing' | 'new_request';
  title: string;
  message: string;
  deploymentId: string;
  deploymentTitle: string;
  timestamp: string;
  read: boolean;
  priority: 'high' | 'medium' | 'low';
  color: 'red' | 'green' | 'yellow' | 'blue';
}

export const useNotifications = (currentUser: User) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = async () => {
    if (!currentUser?.id) return;
    
    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/api/notifications/${currentUser.id}`));
      if (!response.ok) throw new Error('Failed to fetch notifications');
      const serverNotifs = await response.json();

      const mapped: Notification[] = serverNotifs.map((n: any) => ({
        id: n._id,
        type: (n.meta && n.meta.type) || 'new_request',
        title: n.title,
        message: n.message,
        deploymentId: n.meta?.deploymentId || '',
        deploymentTitle: n.meta?.deploymentTitle || '',
        timestamp: n.createdAt,
        read: !!n.read,
        priority: n.meta?.priority || 'low',
        color: n.meta?.color || 'blue'
      }));

      setNotifications(mapped);
      setUnreadCount(mapped.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    // Optimistically update local state
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    // Call server to persist mark as read
    try {
      const response = await fetch(getApiUrl(`/api/notifications/${notificationId}/read`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to mark notification as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Optionally revert local state on error
    }
  };

  const markAllAsRead = async () => {
    // Optimistically update local state
    const unreadNotifIds = notifications.filter(n => !n.read).map(n => n.id);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);

    // Call server for each unread notification to mark as read
    try {
      await Promise.all(unreadNotifIds.map(id => 
        fetch(getApiUrl(`/api/notifications/${id}/read`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })
      ));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [currentUser?.id, currentUser?.role]);

  return {
    notifications,
    unreadCount,
    loading,
    refresh: fetchNotifications,
    markAsRead,
    markAllAsRead
  };
};
